import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import { Button, RotateCcwIcon } from '@popsure/dirty-swan';
import classNames from 'classnames';
import { Dispatch, SetStateAction } from 'react';

import { AdjustPolicyConfig, AdjustPolicyReturnValue } from '../../models';
import { AdjustPolicyOption } from '../Option';
import styles from './AdjustPolicy.module.scss';
import { AdjustPolicyHeader } from './Header';

export const AdjustPolicyView = <Questionnaire extends QuestionnaireQuestions>({
  title,
  checkboxPolicyOptions,
  optionValues,
  setOptionValues,
  newPrice,
  oldPrice,
  reset,
  hasChangedOptions,
  handleSubmit,
  ...props
}: QuestionnaireFormProps<Questionnaire, AdjustPolicyReturnValue> &
  AdjustPolicyConfig & {
    handleSubmit: () => void;
    optionValues: Record<string, any>;
    setOptionValues: Dispatch<SetStateAction<Record<string, any>>>;
    reset: () => void;
    newPrice: number;
    oldPrice: number;
    hasChangedOptions: boolean;
  }) => {
  const priceDelta = newPrice - oldPrice;

  const priceDeltaString =
    priceDelta > 0
      ? `+${englishFormattedEuroCurrency(priceDelta)}`
      : englishFormattedEuroCurrency(priceDelta);

  return (
    <div className="w100">
      <AdjustPolicyHeader title={title} {...props} />
      <div className="p-container mt32">
        <div
          className={classNames(
            styles.options,
            'd-flex fd-column gap32 bg-white br8 p32 relative'
          )}
        >
          <Button
            variant="textColor"
            type="button"
            className={classNames(
              'd-flex ai-center tc-primary-500 fw-bold',
              styles.resetButton,
              {
                'd-none': !hasChangedOptions,
              }
            )}
            onClick={() => reset()}
          >
            <RotateCcwIcon size={20} />
            Reset
          </Button>

          {checkboxPolicyOptions.map(({ questionId, ...optionProps }) => (
            <AdjustPolicyOption
              key={questionId}
              {...optionProps}
              questionId={questionId}
              value={optionValues?.[questionId]}
              setValue={(value) =>
                setOptionValues((prevState: Record<string, any>) => ({
                  ...prevState,
                  [questionId]: value,
                }))
              }
            />
          ))}
        </div>
        <div className="d-flex fd-column bg-white br8 p32 mt24">
          <p className="p-h3 d-flex jc-between ai-center">
            New price
            <span className="p-h2">
              {englishFormattedEuroCurrency(newPrice)}/mo
            </span>
          </p>
          <p className="p-p d-flex jc-between ai-center mt16">
            Current price
            <span>{englishFormattedEuroCurrency(oldPrice)}/mo</span>
          </p>
          <p className="p-p d-flex jc-between ai-center mt8">
            Price change<span>{priceDeltaString}/mo</span>
          </p>
        </div>
        <div className="d-flex jc-center mt24">
          <Button
            className="wmn3"
            onClick={handleSubmit}
            disabled={!hasChangedOptions}
          >
            Continue to details
          </Button>
        </div>
      </div>
    </div>
  );
};
